import {
  func,
  object,
  bool,
  number,
  string,
  oneOfType,
  array,
} from 'prop-types'
import { useState, useContext } from 'react'
import { useUserRole } from '@opallabs/api'
import { kuiperClassNames } from '@opallabs/kuiper'
import { AnalyticsLayer } from '@opallabs/quanta'
import CardContext from '../../../../../cardContext'
import { multiSorter } from '../../../../../helpers'
import ContentListItem from '../ContentListItem'

import './styles.scss'

const ContentList = ({
  activeFilters = [],
  selectedFilters,
  contextMenuCallbacks,
  isMomentDragActive,
  isSelected,
  moment,
  onDragRestrictedContent,
  story,
}) => {
  const { data: userRoleData } = useUserRole()
  const [showTopDropzone, setShowTopDropzone] = useState(false)
  const { isDraggingContent } = useContext(CardContext) ?? {}
  const channelFilters =
    activeFilters?.filter(
      (filter) => filter.includes('channel') && isNaN(filter.slice(8))
    ) || []
  const accountFilters =
    activeFilters?.filter(
      (filter) => filter.includes('channel') && !isNaN(filter.slice(8))
    ) || []
  const labelFilters =
    activeFilters?.filter((filter) => filter.startsWith('label-')) || []

  const matchesLabelFilters = (contentItem) => {
    if (!labelFilters.length || !selectedFilters?.labels) return true
    const directLabels = contentItem.labels || []
    const inheritedLabels =
      directLabels.length > 0
        ? contentItem.inherited_labels || []
        : contentItem.inherited_labels?.length
          ? contentItem.inherited_labels
          : moment.labels || []

    const contentLabels = [...directLabels, ...inheritedLabels]

    /**
     * Label filters are exclusive between label sets, but inclusive within a label set.
     * `selectedFilters.labels` is an object of the shape [labelSetId]: Array<labelId>,
     * so we'll make sure the content has a label from every label set.
     */
    const setsOfFilteredLabelIds = Object.values(selectedFilters.labels)

    return setsOfFilteredLabelIds.every((filteredLabelIdsForSet) => {
      const activeFilterLabelIds = filteredLabelIdsForSet
        .filter(({ disabled = false }) => !disabled)
        .map(({ id }) => id)

      // No filters are active for this set, skip its check
      if (activeFilterLabelIds.length === 0) return true

      return contentLabels.some(({ id: contentLabelId }) =>
        activeFilterLabelIds.includes(contentLabelId)
      )
    })
  }

  let contentList = [...moment.posts]

  const sortBy = contentList.every((c) => c.position !== null)
    ? 'position'
    : 'id'

  contentList.sort(multiSorter([{ sortBy, order: 'ASC' }]))

  const hasChannelOrAccountFilters =
    channelFilters.length || accountFilters.length

  contentList = contentList.filter((contentItem) => {
    if (!contentItem.moment || contentItem.moment.id !== moment.id) return false
    if (!matchesLabelFilters(contentItem)) return false

    if (hasChannelOrAccountFilters) {
      const account = contentItem.account?.id
      return (
        channelFilters.includes('channel-' + contentItem.service.system_name) ||
        accountFilters.includes('channel-' + account)
      )
    }

    return true
  })

  return (
    <div
      className={kuiperClassNames(
        'contentList',
        { 'contentList--selected': isSelected },
        { 'contentList--isSaving': moment.isSaving }
      )}
    >
      {isDraggingContent && (
        <div
          className={kuiperClassNames('contentList-top-dropzone')}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setShowTopDropzone(true)}
          onDragLeave={() => setShowTopDropzone(false)}
        />
      )}
      <div
        className={kuiperClassNames('contentPlaceholder', {
          isContentDropzoneActive: showTopDropzone,
        })}
      />
      {contentList.map((content, idx) => (
        <AnalyticsLayer key={idx} name="Content Item">
          <ContentListItem
            content={content}
            contextMenuCallbacks={contextMenuCallbacks}
            index={idx}
            isLimitedUser={userRoleData?.isRestrictedUser ?? true}
            isMomentDragActive={isMomentDragActive}
            isPlaceholder={content.isPlaceholder}
            moment={moment}
            onDragRestrictedContent={onDragRestrictedContent}
            story={story}
          />
        </AnalyticsLayer>
      ))}
    </div>
  )
}

ContentList.propTypes = {
  activeFilters: array,
  selectedFilters: object,
  contextMenuCallbacks: object,
  isMomentDragActive: bool,
  isSelected: bool,
  moment: object,
  onDragRestrictedContent: func,
  pivotName: oneOfType([number, string]),
  story: object,
}

export default ContentList
